import { useEffect, useMemo } from "react";

type JsonLdObject = Record<string, unknown>;

const DEFAULT_TITLE = "BloXida — Fashion, Health, Travel & Technology Blog";
const DEFAULT_DESCRIPTION =
  "Discover trending articles on fashion, health, travel, and technology. Stay updated with BloXida's expert guides, reviews, and lifestyle content.";
const FALLBACK_SITE_URL = "https://bloxida.com";
const DEFAULT_KEYWORDS = [
  "blog",
  "fashion",
  "health",
  "travel",
  "technology",
  "lifestyle",
];

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  /** Explicit canonical URL. Defaults to origin + pathname (no query string). */
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  category?: string;
  keywords?: string[];
  noIndex?: boolean;
  /** Set true on pages where canonical should not be emitted (e.g. search with ?q=). */
  noCanonical?: boolean;
  jsonLd?: JsonLdObject | JsonLdObject[];
}

/** Try to normalise an arbitrary date string to ISO 8601. Returns undefined on failure. */
function toIso(date?: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(date);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

function getSiteUrl(): string {
  if (typeof window !== "undefined") return window.location.origin;
  return FALLBACK_SITE_URL;
}

function absoluteUrl(pathOrUrl: string, siteUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${siteUrl}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function upsertMeta(
  selector: string,
  attrKey: "name" | "property",
  attrValue: string,
  content: string,
) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrKey, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(selector: string) {
  document.querySelector(selector)?.remove();
}

export function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = "/og-image.jpg",
  url,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  category,
  keywords = DEFAULT_KEYWORDS,
  noIndex = false,
  noCanonical = false,
  jsonLd,
}: SEOProps) {
  const siteUrl = getSiteUrl();
  // Use origin + pathname as canonical to strip query strings (e.g. ?q=search).
  // Callers can override with an explicit `url` prop.
  const canonicalPath =
    typeof window !== "undefined"
      ? `${siteUrl}${window.location.pathname}`
      : siteUrl;
  const fullUrl = absoluteUrl(url || canonicalPath, siteUrl);
  const fullImage = absoluteUrl(image, siteUrl);

  const pubIso = toIso(publishedTime);
  const modIso = toIso(modifiedTime) ?? pubIso;
  const robots = noIndex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large";
  const structuredHeadline = title.replace(/\s[—|-]\sBloXida$/, "");

  // Update <head> meta tags reactively
  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta(
      'meta[name="keywords"]',
      "name",
      "keywords",
      keywords.join(", "),
    );
    upsertMeta('meta[name="robots"]', "name", "robots", robots);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description,
    );
    upsertMeta('meta[property="og:image"]', "property", "og:image", fullImage);
    upsertMeta('meta[property="og:url"]', "property", "og:url", fullUrl);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      "BloXida",
    );
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", "en_US");
    upsertMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description,
    );
    upsertMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      fullImage,
    );

    if (type === "article") {
      if (pubIso)
        upsertMeta(
          'meta[property="article:published_time"]',
          "property",
          "article:published_time",
          pubIso,
        );
      else removeMeta('meta[property="article:published_time"]');
      if (modIso)
        upsertMeta(
          'meta[property="article:modified_time"]',
          "property",
          "article:modified_time",
          modIso,
        );
      else removeMeta('meta[property="article:modified_time"]');
      if (author)
        upsertMeta(
          'meta[property="article:author"]',
          "property",
          "article:author",
          author,
        );
      else removeMeta('meta[property="article:author"]');
      if (category)
        upsertMeta(
          'meta[property="article:section"]',
          "property",
          "article:section",
          category,
        );
      else removeMeta('meta[property="article:section"]');
    } else {
      removeMeta('meta[property="article:published_time"]');
      removeMeta('meta[property="article:modified_time"]');
      removeMeta('meta[property="article:author"]');
      removeMeta('meta[property="article:section"]');
    }

    // Canonical link — skip on pages where query params are semantically meaningful
    // (e.g. /search?q=...) by passing noCanonical={true}.
    let canonical = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (noCanonical) {
      canonical?.remove();
    } else {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", fullUrl);
    }

    // Auto-inject RSS Feed link tag if not present
    let rssLink = document.querySelector(
      'link[type="application/rss+xml"]',
    ) as HTMLLinkElement | null;
    if (!rssLink) {
      rssLink = document.createElement("link");
      rssLink.setAttribute("rel", "alternate");
      rssLink.setAttribute("type", "application/rss+xml");
      rssLink.setAttribute("title", "BloXida RSS Feed");
      rssLink.setAttribute("href", "/feed.xml");
      document.head.appendChild(rssLink);
    }
  }, [
    title,
    description,
    fullUrl,
    fullImage,
    keywords,
    robots,
    noCanonical,
    type,
    pubIso,
    modIso,
    author,
    category,
  ]);

  // Build JSON-LD structured data
  const defaultJsonLd: JsonLdObject = useMemo(() => {
    const organization = {
      "@type": "NewsMediaOrganization",
      "@id": `${siteUrl}/#organization`,
      name: "BloXida",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl("/favicon.svg", siteUrl),
      },
      publishingPrinciples: `${siteUrl}/about`,
    };

    if (type === "article") {
      const breadcrumbList = {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          ...(category
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: category,
                  item: `${siteUrl}/category/${category.toLowerCase()}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: structuredHeadline,
                  item: fullUrl,
                },
              ]
            : [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: structuredHeadline,
                  item: fullUrl,
                },
              ]),
        ],
      };

      return {
        "@context": "https://schema.org",
        "@graph": [
          organization,
          breadcrumbList,
          {
            "@type": "NewsArticle",
            "@id": `${fullUrl}#article`,
            mainEntityOfPage: { "@type": "WebPage", "@id": fullUrl },
            headline: structuredHeadline,
            description,
            image: [fullImage],
            url: fullUrl,
            inLanguage: "en-US",
            ...(pubIso && { datePublished: pubIso }),
            ...(modIso && { dateModified: modIso }),
            ...(author && { author: { "@type": "Person", name: author } }),
            publisher: { "@id": `${siteUrl}/#organization` },
            ...(category && { articleSection: category }),
            ...(keywords.length > 0 && { keywords: keywords.join(", ") }),
          },
        ],
      };
    }

    return {
      "@context": "https://schema.org",
      "@graph": [
        organization,
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          name: "BloXida",
          url: siteUrl,
          description,
          publisher: { "@id": `${siteUrl}/#organization` },
          inLanguage: "en-US",
          potentialAction: {
            "@type": "SearchAction",
            target: `${siteUrl}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "WebPage",
          "@id": fullUrl,
          url: fullUrl,
          name: title,
          description,
          isPartOf: { "@id": `${siteUrl}/#website` },
          inLanguage: "en-US",
        },
      ],
    };
  }, [
    type,
    title,
    structuredHeadline,
    description,
    fullUrl,
    fullImage,
    pubIso,
    modIso,
    author,
    category,
    keywords,
    siteUrl,
  ]);

  const finalJsonLd = useMemo(
    () =>
      jsonLd
        ? Array.isArray(jsonLd)
          ? [defaultJsonLd, ...jsonLd]
          : [defaultJsonLd, jsonLd]
        : defaultJsonLd,
    [defaultJsonLd, jsonLd],
  );

  // Inject JSON-LD into <head> (not <body>) and clean up on unmount / change
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-bloxida-ld", "");
    script.textContent = JSON.stringify(finalJsonLd);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [finalJsonLd]);

  // This component only mutates <head>; nothing rendered into the DOM tree.
  return null;
}
